/**
 * Cart Upsell - handles add to cart for the upsell widget in the cart drawer.
 * Uses event delegation so it works after DOM morphing.
 */
(function () {
  'use strict';

  /** @returns {string[]} */
  function getCartSectionIds() {
    const components = /** @type {NodeListOf<HTMLElement>} */ (
      document.querySelectorAll('cart-items-component[data-section-id]')
    );
    return Array.from(components)
      .map((el) => el.dataset.sectionId)
      .filter(/** @param {string|undefined} id @returns {id is string} */ (id) => Boolean(id));
  }

  /**
   * @param {string} variantId
   * @param {string[]} sectionIds
   * @returns {Promise<Record<string, unknown>>}
   */
  async function addToCart(variantId, sectionIds) {
    const payload = {
      id: Number(variantId),
      quantity: 1,
      sections: sectionIds.join(','),
    };

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = /** @type {Record<string, unknown>} */ (await response.json());

    if (data['status']) {
      const message = /** @type {string} */ (data['description'] || data['message'] || 'Failed to add to cart');
      throw new Error(message);
    }

    return data;
  }

  /** @param {Record<string, unknown>} data */
  function dispatchCartUpdate(data) {
    const sections = /** @type {Record<string, string> | undefined} */ (data['sections']);
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          data: {
            sections,
            source: 'cart-upsell',
          },
        },
      })
    );
  }

  /** @param {HTMLButtonElement} btn */
  function setLoading(btn) {
    const text = btn.querySelector('.cart-upsell__btn-text');
    const loading = btn.querySelector('.cart-upsell__btn-loading');
    btn.disabled = true;
    if (text instanceof HTMLElement) text.hidden = true;
    if (loading instanceof HTMLElement) loading.hidden = false;
  }

  /** @param {HTMLButtonElement} btn */
  function clearLoading(btn) {
    const text = btn.querySelector('.cart-upsell__btn-text');
    const loading = btn.querySelector('.cart-upsell__btn-loading');
    btn.disabled = false;
    if (text instanceof HTMLElement) text.hidden = false;
    if (loading instanceof HTMLElement) loading.hidden = true;
  }

  // Dots follow the slider's scroll position
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (!(track instanceof HTMLElement) || !track.matches('[data-upsell-track]')) return;
      const index = Math.round(track.scrollLeft / track.clientWidth);
      const dots = track.parentElement?.querySelectorAll('[data-upsell-dot]');
      dots?.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    },
    true
  );

  // Dot click → go to slide
  document.addEventListener('click', (event) => {
    const dot = /** @type {HTMLElement} */ (event.target)?.closest('[data-upsell-dot]');
    if (!(dot instanceof HTMLElement)) return;
    const track = dot.closest('[data-cart-upsell]')?.querySelector('[data-upsell-track]');
    if (!(track instanceof HTMLElement)) return;
    track.scrollTo({ left: Number(dot.dataset.upsellDot) * track.clientWidth, behavior: 'smooth' });
  });

  // Drag/swipe to slide (mouse, touch and pen). Snapping is handled here so a short drag is enough to change slide.
  const DRAG_THRESHOLD = 0.15; // fraction of a slide's width
  /** @type {{track: HTMLElement, startX: number, startScroll: number, startIndex: number, moved: boolean, pointerId: number} | null} */
  let drag = null;
  let suppressClick = false;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const track = /** @type {HTMLElement} */ (event.target)?.closest('[data-upsell-track]');
    if (!(track instanceof HTMLElement) || track.children.length < 2) return;
    drag = {
      track,
      startX: event.clientX,
      startScroll: track.scrollLeft,
      startIndex: Math.round(track.scrollLeft / track.clientWidth),
      moved: false,
      pointerId: event.pointerId,
    };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dx = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      drag.track.classList.add('is-dragging');
      drag.track.setPointerCapture(event.pointerId);
    }
    drag.track.scrollLeft = drag.startScroll - dx;
  });

  /** @param {PointerEvent} event */
  function endDrag(event) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const { track, startX, startIndex, moved } = drag;
    drag = null;
    track.classList.remove('is-dragging');
    if (!moved) return;

    suppressClick = true;
    setTimeout(() => (suppressClick = false), 0);

    const width = track.clientWidth;
    const dx = event.clientX - startX;
    let target = startIndex;
    if (Math.abs(dx) > width * DRAG_THRESHOLD) target += dx < 0 ? 1 : -1;
    target = Math.max(0, Math.min(track.children.length - 1, target));
    track.scrollTo({ left: target * width, behavior: 'smooth' });
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  // A drag must not trigger the button it ended on
  document.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return;
      event.stopPropagation();
      event.preventDefault();
    },
    true
  );

  // Add to cart on button click
  document.addEventListener('click', async (event) => {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest('[data-upsell-add-btn]');
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const sectionIds = getCartSectionIds();

    setLoading(btn);

    try {
      const data = await addToCart(variantId, sectionIds);
      dispatchCartUpdate(data);
    } catch (error) {
      console.error('[cart-upsell]', error);
      clearLoading(btn);
    }
  });
})();